import { useEffect, useRef } from "react";

/**
 * Premium gold dot cursor — desktop only.
 * Uses direct DOM mutation (no setState) so it never triggers React re-renders.
 * Two layers: outer ring (slow follower) + inner dot (instant).
 */
export default function CustomCursor() {
  const outerRef = useRef(null);
  const innerRef = useRef(null);
  const pos = useRef({ x: -100, y: -100 });
  const smoothPos = useRef({ x: -100, y: -100 });
  const rafRef = useRef(null);
  const hovering = useRef(false);

  useEffect(() => {
    // Only on non-touch devices
    if (window.matchMedia("(hover: none)").matches) return;

    const outer = outerRef.current;
    const inner = innerRef.current;
    if (!outer || !inner) return;

    document.documentElement.style.cursor = "none";

    const onMove = (e) => {
      pos.current.x = e.clientX;
      pos.current.y = e.clientY;
      // Inner dot: instant
      inner.style.transform = `translate(${e.clientX - 3}px, ${e.clientY - 3}px)`;
    };

    const onEnter = (e) => {
      const el = e.target;
      hovering.current = !!(el.closest("a, button, [role='button'], .project-row, .pd-grid__card"));
    };

    const tick = () => {
      const dx = pos.current.x - smoothPos.current.x;
      const dy = pos.current.y - smoothPos.current.y;
      smoothPos.current.x += dx * 0.12;
      smoothPos.current.y += dy * 0.12;

      const scale = hovering.current ? 1.8 : 1;
      outer.style.transform = `translate(${smoothPos.current.x - 16}px, ${smoothPos.current.y - 16}px) scale(${scale})`;
      outer.style.opacity = hovering.current ? "0.6" : "0.35";

      rafRef.current = requestAnimationFrame(tick);
    };

    window.addEventListener("mousemove", onMove);
    document.addEventListener("mouseover", onEnter);
    rafRef.current = requestAnimationFrame(tick);

    return () => {
      document.documentElement.style.cursor = "";
      window.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseover", onEnter);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <>
      <style>{`
        @media (hover: none) { .cursor-outer, .cursor-inner { display: none; } }
        .cursor-outer {
          position: fixed;
          top: 0; left: 0;
          width: 32px; height: 32px;
          border: 1px solid rgba(212, 164, 92,0.7);
          border-radius: 50%;
          pointer-events: none;
          z-index: 99999;
          will-change: transform;
          transition: transform 0ms, opacity 300ms ease;
        }
        .cursor-inner {
          position: fixed;
          top: 0; left: 0;
          width: 6px; height: 6px;
          background: #d4a45c;
          border-radius: 50%;
          pointer-events: none;
          z-index: 99999;
          will-change: transform;
          box-shadow: 0 0 8px rgba(212, 164, 92,0.8);
        }
      `}</style>
      <div ref={outerRef} className="cursor-outer" aria-hidden="true" />
      <div ref={innerRef} className="cursor-inner" aria-hidden="true" />
    </>
  );
}
